import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import rentsService from "../services/rentsService";
import paymentsService from "../services/paymentsService";
import { useAuth } from "../contexts/AuthContext";
import styles from "../assets/rentPayments.module.css";
import Loader from "../components/Loader";
import s3Service from "../services/s3Service";

const RentPayments = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rentData, setRentData] = useState(null);
  const [guardianData, setGuardianData] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [initialPayments, setInitialPayments] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [guardianImageUrl, setGuardianImageUrl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const lastRefreshRef = useRef(null);
  const searchInputRef = useRef(null);
  const wasSearchFocusedRef = useRef(false);

  // Track when search input gains focus
  const handleSearchFocus = () => {
    wasSearchFocusedRef.current = true;
  };

  // Track when search input loses focus
  const handleSearchBlur = () => {
    setTimeout(() => {
      if (document.activeElement !== searchInputRef.current) {
        wasSearchFocusedRef.current = false;
      }
    }, 0);
  };

  // Maintain focus on search input after rerenders
  useEffect(() => {
    if (wasSearchFocusedRef.current && searchInputRef.current) {
      requestAnimationFrame(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
          const input = searchInputRef.current;
          if (input.setSelectionRange && searchTerm.length > 0) {
            const cursorPos = Math.min(searchTerm.length, input.value.length);
            input.setSelectionRange(cursorPos, cursorPos);
          }
        }
      });
    }
  }, [payments, searchTerm]);

  // Debounce search term to avoid excessive filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce for client-side filtering

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refresh data when rentId changes OR when navigating back (location.key changes on every navigation)
  useEffect(() => {
    const shouldRefresh = 
      // Initial load (no previous ref)
      lastRefreshRef.current === null ||
      // Check if location.key changed (new navigation - React Router changes this on every navigation)
      location.key !== lastRefreshRef.current?.key ||
      // Always refresh when rentId changes
      rentId !== lastRefreshRef.current?.rentId ||
      // Refresh if navigation state explicitly requests it (from PaymentEdit/PaymentCreate)
      (location.state?.refresh === true && location.state?.timestamp !== lastRefreshRef.current?.lastTimestamp);

    if (shouldRefresh) {
      console.log("🔄 [RENTPAYMENTS] Refreshing payment data", {
        reason: lastRefreshRef.current === null ? 'initial load' : 
                location.key !== lastRefreshRef.current?.key ? 'navigation' :
                rentId !== lastRefreshRef.current?.rentId ? 'rentId changed' :
                'refresh flag'
      });
      loadRentData();
      // Update ref to track current state
      lastRefreshRef.current = {
        key: location.key,
        rentId: rentId,
        timestamp: Date.now(),
        lastTimestamp: location.state?.timestamp || null
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentId, location.key, location.pathname]);

  const loadRentData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("🔍 [FRONTEND DEBUG] Loading rent payments for rentId:", rentId);
      
      const data = await rentsService.getRentPayments(rentId);
      console.log("🔍 [FRONTEND DEBUG] API Response:", data);
      
      // Don't throw error if API returns empty data - just show empty state
      // This prevents logout on empty responses
      
      if (data.success && data.rent) {
        setRentData(data.rent);
        setGuardianData(data.guardian || data.rent.guardian || null);
        setPropertyData(data.property || data.rent.room?.property || null);
        setPayments(data.payments || []);
        setInitialPayments(data.initialPayments || []);
        
        // Calculate summary data dynamically based on actual payments
        // FIX: Don't rely on rent.payment_status - calculate from individual payment statuses
        const allPayments = data.payments || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Helper to get payment's actual status
        const getPaymentStatus = (p) => {
          const status = p.original_status !== undefined ? p.original_status : p.status;
          return parseInt(status) || 0;
        };

        // Helper to check if payment is unpaid (status 0)
        const isUnpaid = (p) => getPaymentStatus(p) === 0;

        // Helper to check if payment is overdue (unpaid AND past due date)
        const isOverdue = (p) => {
          if (!isUnpaid(p) || !p.due_date) return false;
          const dueDate = new Date(p.due_date);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate < today;
        };

        // Helper to check if payment is "to pay" (unpaid AND future due date)
        const isToPay = (p) => {
          if (!isUnpaid(p) || !p.due_date) return false;
          const dueDate = new Date(p.due_date);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate >= today;
        };

        // Determine summary status based on actual payments
        // Priority: Overdue (0/red) > To Pay (2/yellow) > Paid (1/green)
        let summaryStatus = 1; // Default to paid
        const hasOverduePayments = allPayments.some(isOverdue);
        const hasToPayPayments = allPayments.some(isToPay);

        if (hasOverduePayments) {
          summaryStatus = 0; // Not paid (red) - has overdue payments
        } else if (hasToPayPayments) {
          summaryStatus = 2; // To pay (yellow) - has future unpaid payments
        } else {
          summaryStatus = 1; // Paid (green) - all payments are paid
        }

        console.log('📊 [RENT PAYMENTS] Summary status calculation:', {
          hasOverduePayments,
          hasToPayPayments,
          summaryStatus,
          paymentsCount: allPayments.length
        });

        // Exclude "To Pay" (future unpaid) payments from summary totals
        const nonToPayPayments = allPayments.filter(p => !isToPay(p));

        const summary = {
          dueDate: data.summary?.dueDate || data.rent?.dueDate ||
                   (allPayments.length > 0 ? allPayments[0].due_date : null),
          totalAmount: nonToPayPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
          status: summaryStatus, // Use calculated status, not stored rent.payment_status
          amountPaid: nonToPayPayments.reduce((sum, p) => sum + (parseFloat(p.amount_paid || p.paid_amount) || 0), 0),
          toPay: nonToPayPayments.reduce((sum, p) => sum + (parseFloat(p.amount || 0) - parseFloat(p.amount_paid || p.paid_amount || 0)), 0)
        };
        console.log('📊 [RENT PAYMENTS] Final summary:', summary);
        setSummaryData(summary);
        
        // Load guardian image from S3 (backend now returns presigned URL directly)
        if (data.guardian?.imageUrl || data.rent.guardian?.imageUrl) {
          // Backend already provides presigned URL in imageUrl field
          setGuardianImageUrl(data.guardian?.imageUrl || data.rent.guardian?.imageUrl);
        } else if (data.guardian?.image || data.rent.guardian?.image) {
          // Fallback: if backend doesn't provide imageUrl, try to generate it (shouldn't happen normally)
          const imageKey = data.guardian?.image || data.rent.guardian?.image;
          try {
            // Try to get URL from backend
            const presignedUrl = await s3Service.getFileUrlFromBackend(imageKey);
            setGuardianImageUrl(presignedUrl);
          } catch (imgErr) {
            console.error("Error loading guardian image:", imgErr);
            setGuardianImageUrl(null);
          }
        }
        
        console.log("🔍 [FRONTEND DEBUG] Rent data:", data.rent);
        console.log("🔍 [FRONTEND DEBUG] Guardian data:", data.guardian || data.rent.guardian);
        console.log("🔍 [FRONTEND DEBUG] Property data:", data.property || data.rent.room?.property);
        console.log("🔍 [FRONTEND DEBUG] Payments:", data.payments);
      } else {
        // Don't throw error - just show empty state
        // This prevents logout on validation or permission errors
        console.warn("⚠️ [FRONTEND] API returned unsuccessful response:", data);
        setRentData(null);
        setGuardianData(null);
        setPropertyData(null);
        setPayments([]);
        setSummaryData(null);
        // Only show error if it's not a permission/validation issue
        if (data.message && !data.message.toLowerCase().includes('permission') && !data.message.toLowerCase().includes('access denied')) {
          setError(data.message || "Failed to load rent payments data");
        } else {
          setError(null); // Don't show error for permission issues, just empty state
        }
      }
    } catch (err) {
      console.error("❌ [FRONTEND ERROR] Error loading rent payments:", err);
      
      // Don't logout on API errors - just show error message
      // Only set error state, don't trigger logout
      const errorMessage = err.response?.data?.message || err.message || "Failed to load rent payments";
      
      // Check if it's an authentication error (401) - handle gracefully
      if (err.response?.status === 401) {
        console.warn("⚠️ [FRONTEND] Authentication error, but not logging out (might be permission issue)");
        setError("Authentication error. Please refresh the page.");
      } else {
        setError(errorMessage);
      }
      
      setRentData(null);
      setGuardianData(null);
      setPropertyData(null);
      setPayments([]);
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    // Laravel config/rents.php payment_status_label mapping:
    // 0 => 'danger' (Not paid - RED), 1 => 'success' (Paid - GREEN), 2 => 'warning' (To pay - YELLOW)
    const statusConfig = {
      '0': { label: 'danger', text: 'Not paid', color: '#d9534f' }, // RED - Missed periods should be red
      '1': { label: 'success', text: 'Paid', color: '#5cb85c' },
      '2': { label: 'warning', text: 'To pay', color: '#f0ad4e' }, // YELLOW - To Pay (December) should be yellow
      '3': { label: 'default', text: 'Ignore', color: '#777' },
      '4': { label: 'info', text: 'Payment Plan', color: '#5bc0de' },
      '5': { label: 'danger', text: 'Legal', color: '#d9534f' },
      '6': { label: 'danger', text: 'Notice to quit', color: '#d9534f' },
      'not_paid': { label: 'danger', text: 'Not paid', color: '#d9534f' }, // RED - past due date
      'paid': { label: 'success', text: 'Paid', color: '#5cb85c' },
      'pending': { label: 'warning', text: 'Pending', color: '#f0ad4e' },
      'to_pay': { label: 'warning', text: 'To pay', color: '#f0ad4e' }, // YELLOW - future due date, not yet due
      'overdue': { label: 'danger', text: 'Overdue', color: '#d9534f' } // RED
    };
    
    // Convert to string to ensure proper lookup (handles both number 0 and string "0")
    const statusStr = String(status);
    const config = statusConfig[statusStr] || statusConfig[status] || { label: 'default', text: statusStr, color: '#777' };
    
    // Debug logging for summary status
    if (statusStr === '0' && config.text !== 'Not paid') {
      console.warn(`⚠️ [RENT PAYMENTS] Status 0 not mapping correctly. Status: ${status}, StatusStr: ${statusStr}, Config:`, config);
    }
    
    return (
      <span
        className={`label label-${config.label}`}
        style={{
          backgroundColor: config.color,
          color: 'white',
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {config.text}
      </span>
    );
  };

  const handleBack = () => {
    navigate("/rents");
  };

  const handleMarkAsPaid = (payment) => {
    // Laravel behavior: Navigate to dedicated mark-as-paid page instead of API call
    // Laravel route format: /rents/{rentId}/payments/{paymentId}/mark-as-paid
    const paymentId = payment.id || payment;
    const paymentRentId = payment.rent_id || rentId;
    
    if (!paymentRentId) {
      toast.error("Cannot mark as paid: Rent ID not found");
      console.error("Missing rentId for payment:", payment);
      return;
    }
    
    if (!paymentId) {
      toast.error("Cannot mark as paid: Payment ID not found");
      console.error("Missing payment id:", payment);
      return;
    }
    
    // Navigate to mark-as-paid page (Laravel: route('payments.mark-as-paid', [$rentId, $payment->id]))
    navigate(`/rents/${paymentRentId}/payments/${paymentId}/mark-as-paid`);
  };

  const handleSendReminder = async (paymentId) => {
    if (!window.confirm("Are you sure you want to send the email?")) {
      return;
    }
    try {
      await paymentsService.sendPaymentReminder(paymentId);
      toast.success("Payment reminder sent successfully");
      loadRentData();
    } catch (err) {
      console.error("Error sending payment reminder:", err);
      toast.error(err.response?.data?.message || "Failed to send payment reminder");
    }
  };

  const handleEditPayment = (paymentId) => {
    navigate(`/rents/${rentId}/payments/${paymentId}/edit`);
  };

  const handleRemovePayment = async (paymentId) => {
    if (!window.confirm("Are you sure you want to remove?")) {
      return;
    }
    try {
      // Laravel route: DELETE /rents/{rentId}/payments/{paymentId}/destroy
      await paymentsService.deletePaymentWithRent(rentId, paymentId);
      toast.success("Payment removed successfully");
      loadRentData();
    } catch (err) {
      console.error("Error removing payment:", err);
      toast.error(err.response?.data?.message || "Failed to remove payment");
    }
  };

  // Helper to check if payment is overdue (Laravel: Payment::isOverdue())
  const isPaymentOverdue = (payment) => {
    if (!payment.due_date) {
      return false;
    }
    // Laravel: Check if payment status is not paid (0) AND due_date is in the past
    const originalStatus = payment.original_status !== undefined ? payment.original_status : payment.status;
    // Laravel only shows overdue for status = 0 (Not paid), not other statuses
    if (originalStatus !== '0' && originalStatus !== 0) {
      return false; // Only status 0 (Not paid) can be overdue
    }
    const dueDate = new Date(payment.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className={styles["rent-payments"]}>
        <div className={styles["error-container"]}>Error: {error}</div>
      </div>
    );
  }

  const currentRent = rentData || { id: rentId };
  const currentGuardian = guardianData || currentRent.guardian || {};
  const currentProperty = propertyData || currentRent.room?.property || {};

  // Filter payments based on search term
  const currentPayments = payments.filter((payment) => {
    if (!debouncedSearchTerm) return true;

    const searchLower = debouncedSearchTerm.toLowerCase();
    const dueDate = formatDate(payment.due_date).toLowerCase();
    const amount = formatCurrency(parseFloat(payment.amount || 0)).toLowerCase();
    const amountPaid = formatCurrency(parseFloat(payment.amount_paid || payment.paid_amount || 0)).toLowerCase();
    const toPay = formatCurrency(parseFloat(payment.amount || 0) - parseFloat(payment.amount_paid || payment.paid_amount || 0)).toLowerCase();
    const bankAccount = (payment.bank_account?.name || payment.bankAccountName || '').toLowerCase();

    // Get status text
    const originalStatus = payment.original_status !== undefined ? payment.original_status : payment.status;
    const paymentStatus = String(originalStatus || '0');
    const isOverdue = isPaymentOverdue(payment) && paymentStatus !== '1';
    const statusConfig = {
      '0': 'not paid',
      '1': 'paid',
      '2': 'to pay',
      '3': 'ignore',
      '4': 'payment plan',
      '5': 'legal',
      '6': 'notice to quit',
    };
    const statusText = (isOverdue ? 'overdue' : (statusConfig[paymentStatus] || paymentStatus)).toLowerCase();

    return (
      dueDate.includes(searchLower) ||
      amount.includes(searchLower) ||
      amountPaid.includes(searchLower) ||
      toPay.includes(searchLower) ||
      bankAccount.includes(searchLower) ||
      statusText.includes(searchLower)
    );
  });

  // Safe render helper
  const safeRender = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      if (value.name) return value.name;
      if (value.fullName) return value.fullName;
      if (value.property_id) return value.property_id;
      if (value.id) return value.id.toString();
      return '-';
    }
    return value.toString();
  };

  return (
    <div className={styles["rent-payments"]}>
      {/* Laravel Breadcrumb: Dashboard / Properties / {Property} / Payment List / {Guardian} / Payments */}
      <div className="breadcrumb-row">
        <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
          <span 
            onClick={() => navigate('/dashboard')} 
            style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
          >
            Dashboard
          </span>
        </span>
        <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
          <span 
            onClick={() => navigate('/properties')} 
            style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
          >
            / Properties
          </span>
        </span>
        {currentProperty.id && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            <span 
              onClick={() => navigate(`/properties/${currentProperty.id}`)} 
              style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
            >
              / {safeRender(currentProperty.name)}
            </span>
          </span>
        )}
        {currentProperty.id && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            <span 
              onClick={() => navigate(`/properties/${currentProperty.id}/payments`)} 
              style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
            >
              / Payment List
            </span>
          </span>
        )}
        {!currentProperty.id && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            / Payment List
          </span>
        )}
        {currentGuardian.id && currentGuardian.fullName && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            <span 
              onClick={() => navigate(`/guardians/${currentGuardian.id}`)} 
              style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
            >
              / {safeRender(currentGuardian.fullName)}
            </span>
          </span>
        )}
        {!currentGuardian.id && currentGuardian.fullName && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            / {safeRender(currentGuardian.fullName)}
          </span>
        )}
        <div style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Payments</span>
        </div>
      </div>

      <div className={styles["main-content"]}>
        {/* Guardian Overview Section - Laravel: @include('guardians._guardian_overview') */}
        <div className={styles["row"]}>
          <div className={styles["col-md-12"]}>
            <div className={styles["panel-title"]}>
              <h3 className={styles["title-standard"]}>Guardian overview</h3>
            </div>
            <div className={styles["panel-group"]}>
              <div className={styles["row"]}>
                <div className={styles["col-sm-6"]}>
                  <div className={styles["guardian-avatar-wrapper"]}>
                    {guardianImageUrl ? (
                      <img className={styles["guardian-avatar"]} src={guardianImageUrl} alt="Guardian" />
                    ) : (
                      <div className={styles["guardian-avatar-placeholder"]}></div>
                    )}
                  </div>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Guardian ID:</dt>
                      <dd>{safeRender(currentGuardian.unique_guardian_number) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Name:</dt>
                      <dd>{safeRender(currentGuardian.fullName) || safeRender(currentRent.guardianName) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Phone number:</dt>
                      <dd>{safeRender(currentGuardian.phone_number || currentGuardian.user?.phone_number) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Mobile number:</dt>
                      <dd>{safeRender(currentGuardian.mobile_number || currentGuardian.user?.mobile_number) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Email:</dt>
                      <dd>
                        <a href={`mailto:${currentGuardian.email || currentGuardian.user?.email || ''}`} className={styles["mailto-link"]}>
                          {safeRender(currentGuardian.email || currentGuardian.user?.email) || '-'}
                        </a>
                      </dd>
                    </div>
                    {currentGuardian.hear_about && (
                      <div className={styles["list-row"]}>
                        <dt title="How did you hear about Global Guardians?">Hear about GG:</dt>
                        <dd>{safeRender(currentGuardian.hear_about)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className={styles["col-sm-6"]}>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Status:</dt>
                      <dd>
                        <span 
                          className={styles["status-label"]}
                          style={{ backgroundColor: currentGuardian.status?.color || '#28a745' }}
                        >
                          {safeRender(currentGuardian.status?.name || currentGuardian.status) || 'Active'}
                        </span>
                      </dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Gender:</dt>
                      <dd>{safeRender(currentGuardian.gender) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt title="Date of Birth">DoB:</dt>
                      <dd>{formatDate(currentGuardian.date_of_birth) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Nationality:</dt>
                      <dd>{safeRender(currentGuardian.nationality || currentGuardian.country?.nationality) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Religion:</dt>
                      <dd>{safeRender(currentGuardian.religion?.name || currentGuardian.religion) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt title="National Insurance Number">NI:</dt>
                      <dd>{safeRender(currentGuardian.national_insurance_number || currentGuardian.nhs_number) || '-'}</dd>
                    </div>
                    {currentGuardian.next_of_kin && (
                      <div className={styles["list-row"]}>
                        <dt>Next of kin</dt>
                        <dd>{safeRender(currentGuardian.next_of_kin)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section - Laravel: @include('payments._property_and_room_details') */}
        <div className={styles["row"]}>
          <div className={styles["col-xs-12"]}>
            <div className={styles["panel-title"]}>
              <h3 className={styles["title-standard"]}>Details</h3>
            </div>
            <div className={styles["panel-group"]}>
              <div className={styles["row"]}>
                <div className={styles["col-xs-6"]}>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Property ID:</dt>
                      <dd>{safeRender(currentProperty.property_id || currentProperty.property_public_id) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Property Name:</dt>
                      <dd>{safeRender(currentProperty.name) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Property Status:</dt>
                      <dd>{safeRender(currentProperty.status_text || currentProperty.statusText) || 'Active'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Property address:</dt>
                      <dd>{safeRender(currentProperty.address_1) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Postcode:</dt>
                      <dd>{safeRender(currentProperty.postcode) || '-'}</dd>
                    </div>
                    {currentProperty.date_taken_on && (
                      <div className={styles["list-row"]}>
                        <dt>Lease dates:</dt>
                        <dd>
                          {formatDate(currentProperty.date_taken_on)}
                          {currentProperty.date_taken_back && ` to ${formatDate(currentProperty.date_taken_back)}`}
                        </dd>
                      </div>
                    )}
                    {currentProperty.date_notice && (
                      <div className={styles["list-row"]}>
                        <dt>Notice date:</dt>
                        <dd>{formatDate(currentProperty.date_notice)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className={styles["col-xs-6"]}>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Client</dt>
                      <dd>{safeRender(currentProperty.client?.name) || '-'}</dd>
                    </div>
                    {currentProperty.client?.bankAccount && (
                      <div className={styles["list-row"]}>
                        <dt>GG Bank Account</dt>
                        <dd>{safeRender(currentProperty.client.bankAccount.name)}</dd>
                      </div>
                    )}
                    <div className={styles["list-row"]}>
                      <dt>Room:</dt>
                      <dd>{safeRender(currentRent.room?.name || currentRent.roomName) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Rate:</dt>
                      <dd>£{currentRent.monthly_payment?.toFixed(2) || '0.00'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Start:</dt>
                      <dd>{formatDate(currentRent.date_start) || formatDate(currentRent.dateStartShow) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>End:</dt>
                      <dd>{formatDate(currentRent.date_end) || formatDate(currentRent.dateEndShow) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Status:</dt>
                      <dd>
                        {currentRent.status === 0 ? 'In progress' :
                         currentRent.status === 1 ? 'Active' :
                         currentRent.status === 2 ? 'Failed' :
                         currentRent.status === 3 ? 'Ended' :
                         safeRender(currentRent.status_text) || 'Active'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Initial Payments Section (Laravel parity) */}
        {initialPayments.length > 0 && (
          <div className={styles["row"]}>
            <div className={styles["col-md-12"]}>
              <div className={styles["panel-title"]}>
                <h3>Initial Payments / Key Fees</h3>
              </div>
              <div className={styles["panel-group"]}>
                <div className={styles["dataTable-wrapper"]}>
                  <table className={`${styles["table"]} ${styles["table-hover"]} ${styles["dt-responsive"]}`}>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Due Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Amount Paid</th>
                        <th>To Pay</th>
                        <th>Bank</th>
                        <th className={styles["action-head"]}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {initialPayments.map((ip) => {
                        const amount = parseFloat(ip.amount || 0);
                        const amountPaid = parseFloat(ip.amount_paid || 0);
                        const toPay = amount - amountPaid;
                        const isNotPaid = ip.original_status === 0 || ip.original_status === '0' ||
                                          ip.status === 'not_paid' || ip.status === 'to_pay' ||
                                          ip.status === 'Not paid' || ip.status === 'To pay';

                        return (
                          <tr key={`ip-${ip.id}`}>
                            <td>{ip.type}</td>
                            <td>{formatDate(ip.due_date)}</td>
                            <td>{formatCurrency(amount)}</td>
                            <td>{getStatusBadge(ip.status)}</td>
                            <td>{formatCurrency(amountPaid)}</td>
                            <td>{formatCurrency(toPay)}</td>
                            <td>{ip.bank_account || '-'}</td>
                            <td>
                              {hasPermission("payments.manage") && (
                                <div className={styles["action-buttons"]}>
                                  {isNotPaid && (
                                    <button
                                      className={`${styles["btn"]} ${styles["btn-sm"]} ${styles["btn-success"]}`}
                                      onClick={() => handleMarkAsPaid(ip)}
                                      title="Mark as Paid"
                                    >
                                      Mark as paid
                                    </button>
                                  )}
                                  <a
                                    href={`/rents/${rentId}/payments/${ip.id}/edit?type=initial`}
                                    className={`${styles["btn"]} ${styles["btn-sm"]} ${styles["btn-primary"]}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(`/rents/${rentId}/payments/${ip.id}/edit?type=initial`);
                                    }}
                                  >
                                    Edit
                                  </a>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments Section - Laravel: payments/rents/list.blade.php */}
        <div className={styles["row"]}>
          <div className={styles["col-md-12"]}>
            <div className={styles["panel-title"]}>
              <h3 className={styles["mr-auto"]}>Payments</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {/* Search Input */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="payment-search-input" style={{ margin: 0, fontSize: '14px' }}>Search:</label>
                  <input
                    ref={searchInputRef}
                    id="payment-search-input"
                    type="search"
                    className="form-control"
                    style={{ width: '200px', height: '34px' }}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      wasSearchFocusedRef.current = true;
                    }}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    placeholder="Search payments..."
                    autoFocus={false}
                  />
                </div>
                {hasPermission("payments.manage") && (
                  <a
                    href={`/rents/${rentId}/payments/create`}
                    className={`${styles["btn"]} ${styles["btn-secondary"]}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/rents/${rentId}/payments/create`);
                    }}
                  >
                    <i className={`glyphicon glyphicon-plus`}></i> Add payment
                  </a>
                )}
              </div>
            </div>
            <div className={styles["panel-group"]}>
              {/* Search Results Indicator */}
              {debouncedSearchTerm && (
                <div style={{ padding: '10px 0', fontSize: '14px', color: '#666' }}>
                  Showing {currentPayments.length} of {payments.length} payment{payments.length !== 1 ? 's' : ''}
                  {currentPayments.length !== payments.length && ` matching "${debouncedSearchTerm}"`}
                </div>
              )}

              {/* Summary Row - Laravel: Summary row above table */}
              {summaryData && (
                <div className={styles["mb-30"]}>
                  <div className={styles["row"]}>
                    <div className={styles["col-xs-2"]}><strong>SUMMARY</strong></div>
                    <div className={styles["col-xs-2"]}><strong>Due Date</strong></div>
                    <div className={styles["col-xs-2"]}><strong>Amount</strong></div>
                    <div className={styles["col-xs-2"]}><strong>Status</strong></div>
                    <div className={styles["col-xs-2"]}><strong>Amount Paid</strong></div>
                    <div className={styles["col-xs-2"]}><strong>To Pay</strong></div>
                  </div>
                  <div className={styles["row"]}>
                    <div className={styles["col-xs-2"]}></div>
                    <div className={styles["col-xs-2"]}>{summaryData.dueDate ? formatDate(summaryData.dueDate) : '-'}</div>
                    <div className={styles["col-xs-2"]}>{formatCurrency(summaryData.totalAmount)}</div>
                    <div className={styles["col-xs-2"]}>
                      {summaryData.status !== null && summaryData.status !== undefined 
                        ? getStatusBadge(String(summaryData.status)) 
                        : getStatusBadge('0')}
                    </div>
                    <div className={styles["col-xs-2"]}>{formatCurrency(summaryData.amountPaid)}</div>
                    <div className={styles["col-xs-2"]}>{formatCurrency(summaryData.toPay)}</div>
                  </div>
                </div>
              )}

              {/* Payments Table */}
              {currentPayments.length > 0 ? (
                <div className={styles["dataTable-wrapper"]}>
                  <table className={`${styles["table"]} ${styles["table-hover"]} ${styles["dt-responsive"]}`}>
                    <thead>
                      <tr>
                        <th>Due Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Amount Paid</th>
                        <th>To Pay</th>
                        <th>Bank</th>
                        <th className={styles["action-head"]}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPayments.map((payment) => {
                        // Use transformed status from backend (considers due_date for to_pay vs not_paid)
                        // Backend sends: "to_pay" (future due), "not_paid" (past due), "paid", etc.
                        const transformedStatus = payment.status || 'not_paid';
                        const originalStatus = payment.original_status !== undefined ? payment.original_status : payment.status;
                        const amountPaid = parseFloat(payment.amount_paid || payment.paid_amount || 0);
                        const amount = parseFloat(payment.amount || 0);
                        const toPay = amount - amountPaid;
                        // isNotPaid: original DB status is 0 OR transformed status is not_paid/to_pay
                        const isNotPaid = originalStatus === 0 || originalStatus === '0' ||
                                          transformedStatus === 'not_paid' || transformedStatus === 'to_pay';
                        // isOverdue: transformed status is not_paid (past due date)
                        const isOverdue = transformedStatus === 'not_paid';

                        return (
                          <tr key={payment.id}>
                            <td>{formatDate(payment.due_date)}</td>
                            <td>{formatCurrency(amount)}</td>
                            <td>{getStatusBadge(transformedStatus)}</td>
                            <td>{formatCurrency(amountPaid)}</td>
                            <td>{formatCurrency(toPay)}</td>
                            <td>{safeRender(payment.bank_account?.name || payment.bankAccountName) || '-'}</td>
                            <td>
                              {hasPermission("payments.manage") && (
                                <div className={styles["action-buttons"]}>
                                  {isNotPaid && (
                                    <button
                                      className={`${styles["btn"]} ${styles["btn-sm"]} ${styles["btn-success"]}`}
                                      onClick={() => handleMarkAsPaid(payment)}
                                      title="Mark as Paid"
                                    >
                                      Mark as paid
                                    </button>
                                  )}
                                  {isNotPaid && isOverdue && (
                                    <a
                                      href="#"
                                      className={`${styles["btn"]} ${styles["btn-sm"]} ${styles["btn-info"]} ${styles["btn-action"]}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleSendReminder(payment.id);
                                      }}
                                    >
                                      Send Reminder
                                    </a>
                                  )}
                                  <a
                                    href={`/rents/${rentId}/payments/${payment.id}/edit`}
                                    className={`${styles["btn"]} ${styles["btn-sm"]} ${styles["btn-primary"]}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleEditPayment(payment.id);
                                    }}
                                  >
                                    Edit
                                  </a>
                                  <form 
                                    style={{ display: 'inline' }}
                                    onSubmit={(e) => {
                                      e.preventDefault();
                                      handleRemovePayment(payment.id);
                                    }}
                                  >
                                    <button 
                                      type="submit"
                                      className={`${styles["btn"]} ${styles["btn-sm"]} ${styles["btn-danger"]}`}
                                      data-confirm-action="Are you sure you want to remove?"
                                    >
                                      Remove
                                    </button>
                                  </form>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles["no-payments"]}>
                  <p>No payment!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentPayments;
